import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Loading, Select } from '@/components/ui/daisy';
import { ExamTypeEnum } from '@/models/Exam';

import useCoursePathAssessments from '@/data/hook/evaluation/useCoursePathAssessments';
import useExams, { BaseExamCoursePath } from '@/data/hook/exam/useExams';
import { AssessmentItemTypeEnum } from '@/models/Assessment';
import { ExamFormFields } from './ExamForm';
import { Nullable } from '@/models/Model';

type AssessmentItemFieldsProps = {
  coursePath: BaseExamCoursePath;
  courseLineId: number;
  currentExamId: Nullable<number>;
};

export default function AssessmentItemFields({
  coursePath,
  courseLineId,
  currentExamId,
}: AssessmentItemFieldsProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.examForm.form',
  });
  const coursePathId = coursePath.id;

  const {
    setValue,
    register,
    control,
    formState: { errors },
  } = useFormContext<ExamFormFields>();

  const assessmentIdValue = useWatch({
    control,
    name: 'assessmentId',
  });

  const { assessments, isLoading } = useCoursePathAssessments({
    coursePathId,
    courseLineId,
  });

  const { exams } = useExams({ coursePath });

  const usedAssessmentItemIds = useMemo(() => {
    const ids = new Set<number>();
    for (const e of exams) {
      if (e.id === currentExamId) continue;
      if (e.assessmentItem != null) ids.add(e.assessmentItem.id);
    }
    return ids;
  }, [exams, currentExamId]);

  const assessmentItems = useMemo(() => {
    const assessment = assessments.find(a => a.id === assessmentIdValue);
    const items =
      assessment?.items.filter(
        ({ type }) => type === AssessmentItemTypeEnum.TEST,
      ) ?? [];
    return items;
  }, [assessments, assessmentIdValue]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-7">
        <Loading variant="spinner" color="primary" />
      </div>
    );
  }
  return (
    <div className="flex gap-4">
      <Select.Fieldset
        className="flex-1"
        label={t('assessment')}
        labelAlt={errors?.assessmentId?.message}
      >
        <Select
          className="w-full"
          color={!errors?.assessmentId?.message ? 'default' : 'error'}
          {...register('assessmentId', {
            valueAsNumber: true,
            validate: (value, fields) =>
              fields.type === ExamTypeEnum.SIMULATED ||
              value !== null ||
              t('assessmentRequired'),
            onChange: () => setValue('assessmentItemId', null),
          })}
        >
          <Select.Option value="">{t('assessmentPlaceholder')}</Select.Option>
          {assessments.map(a => (
            <Select.Option key={a.id} value={a.id}>
              {a.name}
            </Select.Option>
          ))}
        </Select>
      </Select.Fieldset>

      <Select.Fieldset
        className="flex-1"
        label={t('assessmentItem')}
        labelAlt={errors?.assessmentItemId?.message}
      >
        <Select
          className="w-full"
          color={!errors?.assessmentItemId?.message ? 'default' : 'error'}
          disabled={!assessmentIdValue}
          {...register('assessmentItemId', {
            valueAsNumber: true,
            validate: (value, fields) => {
              if (fields.type === ExamTypeEnum.SIMULATED) return true;
              if (value === null) return t('assessmentItemRequired');
              if (usedAssessmentItemIds.has(value))
                return t('assessmentItemAlreadyInUse');
              return true;
            },
          })}
        >
          <Select.Option value="" disabled>
            {t('assessmentItemPlaceholder')}
          </Select.Option>
          {assessmentItems.map(it => (
            <Select.Option key={it.id} value={it.id}>
              {it.name}
            </Select.Option>
          ))}
        </Select>
      </Select.Fieldset>
    </div>
  );
}
